/**
 * When a thread should reach further back, and when it must not.
 *
 * Every thread in the app now opens on a bounded page — the newest one — so
 * older messages are reachable rather than loaded. Three screens need the same
 * answer to "is the reader looking for history?", and they had three different
 * ones: the one-to-one thread offered a button, and the group and practice
 * threads had no way back at all because they still loaded everything.
 *
 * A pure function of the scroll position and what is already known, so the rule
 * can be tested without a renderer. The screens supply the numbers; nothing here
 * imports React, and nothing here knows which thread it is deciding for.
 *
 * ── The four ways this goes wrong ────────────────────────────────────────────
 *
 * Each guard below exists because omitting it produces a specific, visible bug:
 *
 *  - no `loading` guard: a scroll gesture fires this on every frame, so one
 *    flick issues a dozen identical requests for the same page;
 *  - no `hasOlder` guard: reaching the true start of a thread asks for the page
 *    before the first message for ever, once per frame, and every answer is
 *    empty — the loop this is most likely to be written without;
 *  - no `hasAnchor` guard: with nothing held there is no message to page back
 *    FROM, and a cursorless request is a request for the newest page, which the
 *    screen already has;
 *  - and the position itself is the LIST's to judge, through `onEndReached`:
 *    a screen reading offsets would need to know which geometry it is in,
 *    and the practice transcript changes geometry mid-session.
 */

export type LoadOlderInput = {
  /** A page is already in flight. */
  loading: boolean;
  /** The last page came back full, so there is probably more behind it. */
  hasOlder: boolean;
  /** There is a message to page back from. */
  hasAnchor: boolean;
};

/**
 * The guards that decide whether reaching the older end may become a request.
 *
 * "The reader is reaching for history" is no longer an offset this module
 * judges: every thread is inverted once it has a conversation, its older end
 * is the scroll END, and the list reports reaching it through `onEndReached` —
 * the virtualized list computes the distance itself, against its own render
 * window. What that callback cannot know is whether a page is already in
 * flight, whether the thread has a start, and whether there is a message to
 * page back from. That is this function, and every guard is load-bearing for
 * the reasons in the module comment.
 */
export function canLoadOlder(input: LoadOlderInput): boolean {
  if (input.loading) return false;
  if (!input.hasOlder) return false;
  if (!input.hasAnchor) return false;
  return true;
}

/**
 * Whether a page that just arrived implies there is more behind it.
 *
 * A SHORT page means the database ran out, which is the only honest end-signal
 * available: asking again and getting nothing would work too, but it costs a
 * round trip every time a reader reaches the start of a thread.
 *
 * An OVER-full page is treated as "more", not as an error. It cannot happen
 * against our own API, and guessing that the server has changed its mind about
 * page size is a worse failure than one redundant request.
 */
export function moreBehind(received: number, pageSize: number): boolean {
  return received >= pageSize;
}
